/**
 * Patches Element.prototype.getBoundingClientRect, HTMLElement.offsetWidth
 * and HTMLElement.offsetHeight with a monotonic clamp that blocks small
 * upward drift (<3px) per element.
 *
 * Background: at non-1.0 devicePixelRatio (browser zoom != 100% OR Windows
 * display scaling 125% / 150% / 175%) DOM measurements come back as
 * fractional pixels because of subpixel render snapping. antd 6 tables have
 * `borderInlineEnd: 1px` on each cell; at dpr 1.5 that border renders as
 * ~1.333 CSS px. `@rc-component/resize-observer` (used by antd) reads both
 * `gBR.width` and `target.offsetWidth`, propagates them to antd's
 * `onColumnResize`, which writes them back into the colgroup. The next
 * render starts from the bigger width, the border still adds ~1px, and so
 * on — a runaway feedback loop growing the table ~1px per tick.
 *
 * The monotonic clamp lets large changes through (window resize, data
 * refresh) but blocks the small-step drift that drives the loop. Values are
 * passed through as-is (still fractional); when growth is below the
 * threshold the cached previous value is returned instead. Keeping the
 * fractional values intact is critical: rounding here was previously
 * desynchronising plain-table sticky headers from the body because antd
 * applied a rounded width to the header colgroup while the body kept
 * rendering at the fractional value. Position (x, y) is also left untouched
 * so absolute positioning math (popovers, scroll calculations,
 * drag-and-drop) stays exact.
 *
 * For the same reason, inside antd tables (at EVERY dpr) the `offsetWidth`
 * getter returns the fractional getBoundingClientRect width instead of the
 * browser's rounded integer. rc-table reads `offsetWidth` to size the header
 * colgroup (Body/MeasureCell on mount + the resize-observer width merge at
 * runtime); the rounded integer is the residual ~1px header/body desync —
 * present even at integer dpr, because a stretched table lays cells out at
 * fractional css px. Because that fractional value can itself feed the
 * runaway-growth loop, this path clamps regardless of dpr (alwaysClamp), unlike
 * the gBR channel which only clamps at fractional dpr. See the getter below.
 */
interface DriftEntry {
  value: number;
  growthStreak: number;
}
const gbrWidthCache = new WeakMap<Element, DriftEntry>();
const gbrHeightCache = new WeakMap<Element, DriftEntry>();
const offsetWidthCache = new WeakMap<Element, DriftEntry>();
const offsetHeightCache = new WeakMap<Element, DriftEntry>();
const tableScopeCache = new WeakMap<Element, boolean>();
// A growth larger than this is always legitimate (real resize, data change) —
// passes through and resets the streak counter. Smaller growths participate
// in the streak detector below.
const LEGITIMATE_GROWTH_PX = 3;
// Once this many consecutive small upward growths happen on the same element
// without any non-growth break, treat it as drift and start clamping. One
// small growth is allowed through (legitimate subpixel adjustment); a
// follow-up growth in the same direction is what reveals the feedback loop.
const DRIFT_STREAK_LIMIT = 2;

// Only clamp inside antd tables. Globally clamping false-positives on small
// legitimate growth in other widgets (e.g. antd Overflow's responsive +N
// badge measures children with tiny width deltas to decide what fits).
function isInTable(el: Element): boolean {
  const cached = tableScopeCache.get(el);
  if (cached !== undefined) return cached;
  const result = el.closest?.('.ant-table-wrapper') !== null;
  tableScopeCache.set(el, result);
  return result;
}

// At integer devicePixelRatio (1.0, 2.0) CSS pixels map 1:1 to device pixels,
// borders render as integer widths, and the subpixel feedback loop that this
// patch exists to break cannot occur. Skipping the clamp at integer dpr keeps
// every legitimate antd resize-observer reading flowing through, which fixes
// the residual ~1px sticky-header/body desync visible in plain tables after
// FILL expansion. Clamp stays active at fractional dpr where the drift loop
// is still possible.
function isIntegerDpr(): boolean {
  const dpr = typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1;
  return Math.abs(dpr - Math.round(dpr)) < 0.01;
}

// `alwaysClamp` forces the anti-drift clamp on even at integer dpr. The
// integer-dpr skip below is safe only when the measured value is the browser's
// rounded INTEGER offsetWidth (an integer can't drift by sub-pixel steps). The
// offsetWidth getter now reports the FRACTIONAL width, which can feed the
// runaway-growth loop at any dpr — so that caller passes alwaysClamp=true.
function clampGrowth(el: Element, value: number, cache: WeakMap<Element, DriftEntry>, alwaysClamp = false): number {
  if (!isInTable(el)) return value;
  if (!alwaysClamp && isIntegerDpr()) return value;
  const entry = cache.get(el);
  if (entry === undefined) {
    cache.set(el, { value, growthStreak: 0 });
    return value;
  }
  const growth = value - entry.value;
  if (growth <= 0 || growth >= LEGITIMATE_GROWTH_PX) {
    // Shrink, no change, or clearly legitimate jump — accept, reset streak.
    cache.set(el, { value, growthStreak: 0 });
    return value;
  }
  // Small upward growth: first one in a row is allowed (lets legitimate
  // subpixel re-layout flow through), subsequent ones are treated as the
  // drift feedback loop and held to the previous value.
  const streak = entry.growthStreak + 1;
  if (streak >= DRIFT_STREAK_LIMIT) {
    cache.set(el, { value: entry.value, growthStreak: streak });
    return entry.value;
  }
  cache.set(el, { value, growthStreak: streak });
  return value;
}

export function patchTableMeasurements(): void {
  if (typeof Element === 'undefined') return;
  const proto = Element.prototype as Element & { __xmRounded?: boolean; };
  if (proto.__xmRounded) return;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const original = proto.getBoundingClientRect;
  proto.getBoundingClientRect = function (this: Element) {
    const r = original.call(this);
    const rw = clampGrowth(this, r.width, gbrWidthCache);
    const rh = clampGrowth(this, r.height, gbrHeightCache);
    if (rw === r.width && rh === r.height) return r;
    return new DOMRect(r.x, r.y, rw, rh);
  };
  proto.__xmRounded = true;

  // rc-resize-observer reads BOTH gBR.width AND target.offsetWidth and flips
  // state when either changes — patching only gBR leaves offsetWidth as a
  // second drift channel.
  const htmlProto = HTMLElement.prototype as HTMLElement & { __xmOffsetRounded?: boolean; };
  if (!htmlProto.__xmOffsetRounded) {
    const wDesc = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'offsetWidth');
    const hDesc = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'offsetHeight');
    if (wDesc?.get) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const origGet = wDesc.get;
      Object.defineProperty(HTMLElement.prototype, 'offsetWidth', {
        configurable: true,
        get(this: HTMLElement) {
          // Inside an antd table, report the exact fractional border-box width
          // (raw getBoundingClientRect width) instead of the browser's rounded
          // integer. rc-table feeds offsetWidth into the *header* colgroup
          // (Body/MeasureCell on mount, plus the resize-observer's `offsetWidth
          // === Math.round(width) ? width : offsetWidth` merge at runtime); the
          // rounded integer is what desyncs the header from the fractional body.
          // This applies at EVERY dpr — a stretched table lays cells out at
          // fractional css px even at integer dpr — and must NOT be snapped to
          // the device grid (snapping moves the value off the cell's real edge
          // and makes the desync worse). Because the value is now fractional it
          // can drive the runaway-growth loop at any dpr, so clamp with
          // alwaysClamp=true (the gBR-channel's integer-dpr skip would let the
          // loop through here). Using the unpatched `original` avoids double
          // clamping through the patched getBoundingClientRect.
          if (isInTable(this)) {
            const gbrWidth = original.call(this).width;        // fractional border-box — but transform-scaled
            const layoutWidth = origGet.call(this) as number;  // rounded border-box — transform-INDEPENDENT
            // A CSS transform on an ancestor (e.g. antd modal zoom:
            // scale(0.2)->scale(1)) scales getBoundingClientRect but NOT
            // offsetWidth. Returning the scaled gBR here would feed rc-table's
            // header colgroup transient scaled widths at mount and desync it from
            // the body (measured off-screen on document.body, unscaled), with no
            // ResizeObserver firing on transform-end to correct it — a
            // non-deterministic desync that differs each modal open. When
            // untransformed, gBR and the layout width differ by <1px (sub-pixel
            // rounding only); a larger gap means a scale transform is active, so
            // fall back to the transform-independent layout width and keep
            // measurements correct mid-animation.
            if (Math.abs(gbrWidth - layoutWidth) > 1) return layoutWidth;
            return clampGrowth(this, gbrWidth, offsetWidthCache, true);
          }
          return origGet.call(this) as number;
        },
      });
    }
    if (hDesc?.get) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const origGet = hDesc.get;
      Object.defineProperty(HTMLElement.prototype, 'offsetHeight', {
        configurable: true,
        get(this: HTMLElement) { return clampGrowth(this, origGet.call(this) as number, offsetHeightCache); },
      });
    }
    htmlProto.__xmOffsetRounded = true;
  }
}
